import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  alpha,
  Paper,
  Divider,
  CircularProgress,
  ThemeProvider,
  createTheme,
  useTheme as useMuiTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Fab,
  Tooltip
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Groups as GroupsIcon,
  Accessibility as AccessibilityIcon,
  CheckCircle as CheckCircleIcon,
  RestartAlt as RestartAltIcon,
  Add as AddIcon
} from '@mui/icons-material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socketService';
import api from '../services/api';
import { diagnosticarUsuario, podeTransferir } from '../utils/authUtils';

// Importando componentes do painel de recepção
import SenhasAtivasList from '../components/painel-recepcao/SenhasAtivasList';
import SenhasAtendimentoList from '../components/painel-recepcao/SenhasAtendimentoList';
import StatsCard from '../components/Dashboard/StatsCard';

// Importando a fonte Montserrat via CSS
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';

// Criando um tema personalizado com a fonte Montserrat
const theme = createTheme({
  typography: {
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
    },
    h2: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
    },
    h3: {
      fontFamily: 'Montserrat',
      fontWeight: 600,
    },
    h4: {
      fontFamily: 'Montserrat',
      fontWeight: 600,
    },
    h5: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
    },
    h6: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
    },
    button: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
    },
  },
});

const PainelRecepcao = () => {
  const baseTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  
  // Combinando temas - mantendo cores e configurações existentes com a nova tipografia
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      ...theme.typography,
    },
  });
  
  // Estado para controlar se a sidebar está aberta ou fechada
  const { sidebarOpen, toggleSidebar } = useSidebar();
  
  // Estado para armazenar as senhas ativas (aguardando) e em atendimento/chamadas
  const [senhasAtivas, setSenhasAtivas] = useState([]);
  const [senhasEmAtendimento, setSenhasEmAtendimento] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  
  // Estado para controlar o diálogo de confirmação do reset
  const [dialogResetOpen, setDialogResetOpen] = useState(false);
  
  // Estado para controlar o loading da transferência
  const [transferindo, setTransferindo] = useState(false);
  
  // Estado para armazenar as estatísticas
  const [estatisticas, setEstatisticas] = useState({
    tempoMedioEspera: '0 min',
    senhasChamadasHoje: 0,
    preferenciaisEspera: 0
  });
  
  // Função para verificar se o usuário pode executar ações administrativas
  const podeExecutarAcaoAdministrativa = () => {
    return user && user.cargo !== 'ATENDENTE';
  };
  
  // Função para buscar senhas
  const buscarSenhas = useCallback(async () => {
    try {
      setCarregando(true);
      
      // Buscar senhas aguardando
      const responseAguardando = await api.get('/senhas/aguardando');
      const dataAguardando = await responseAguardando.data;
      
      // Buscar senhas em atendimento e chamadas
      const responseAtendimento = await api.get('/senhas/em-atendimento');
      const dataAtendimento = await responseAtendimento.data;
      
      // Buscar senhas chamadas
      const responseChamadas = await api.get('/senhas/chamadas');
      const dataChamadas = await responseChamadas.data;
      
      console.log('Resposta senhas aguardando:', dataAguardando);
      console.log('Resposta senhas em atendimento:', dataAtendimento);
      console.log('Resposta senhas chamadas:', dataChamadas);
      
      // Debug: verificar status das senhas
      console.log('Status das senhas chamadas:', dataChamadas.data?.map(s => ({ id: s.id, status: s.status })));
      console.log('Status das senhas em atendimento:', dataAtendimento.data?.map(s => ({ id: s.id, status: s.status })));
      
      if (dataAguardando.success && dataAtendimento.success && dataChamadas.success) {
        // Ordenar senhas ativas por prioridade e tempo de espera
        const senhasAtivasOrdenadas = dataAguardando.data.sort((a, b) => {
          // Primeiro critério: senhas preferenciais vêm primeiro
          if (a.preferencial && !b.preferencial) return -1;
          if (!a.preferencial && b.preferencial) return 1;
          
          // Segundo critério: tempo de emissão
          const tempoA = new Date(a.emitida_em).getTime();
          const tempoB = new Date(b.emitida_em).getTime();
          return tempoA - tempoB;
        });
        
        // Combinar senhas em atendimento e chamadas, marcando o tipo de cada uma
        // Filtrar apenas senhas que estão realmente chamadas (não finalizadas)
        const senhasChamadasComTipo = dataChamadas.data
          .filter(senha => senha.status === 'CHAMADA')
          .map(senha => ({
            ...senha,
            tipoStatus: 'CHAMADA'
          }));
        
        // Filtrar apenas senhas que estão realmente em atendimento (não finalizadas)
        const senhasAtendimentoComTipo = dataAtendimento.data
          .filter(senha => senha.status === 'EM_ATENDIMENTO')
          .map(senha => ({
            ...senha,
            tipoStatus: 'EM_ATENDIMENTO'
          }));
        
        const todasSenhasAtendimento = [...senhasChamadasComTipo, ...senhasAtendimentoComTipo];
        
        // Debug: verificar resultados após filtragem
        console.log(`Senhas chamadas filtradas: ${senhasChamadasComTipo.length} de ${dataChamadas.data?.length || 0}`);
        console.log(`Senhas em atendimento filtradas: ${senhasAtendimentoComTipo.length} de ${dataAtendimento.data?.length || 0}`);
        console.log('Total senhas para exibir:', todasSenhasAtendimento.length);
        
        // Ordenar por prioridade (preferenciais primeiro) e depois por tempo
        const senhasAtendimentoOrdenadas = todasSenhasAtendimento.sort((a, b) => {
          // Primeiro critério: senhas preferenciais vêm primeiro
          if (a.preferencial && !b.preferencial) return -1;
          if (!a.preferencial && b.preferencial) return 1;
          
          // Segundo critério: tempo de chamada/início do atendimento
          const tempoA = new Date(a.chamada_em || a.atendimento_iniciado_em || a.emitida_em).getTime();
          const tempoB = new Date(b.chamada_em || b.atendimento_iniciado_em || b.emitida_em).getTime();
          return tempoA - tempoB;
        });
        
        console.log('Senhas ativas ordenadas:', senhasAtivasOrdenadas);
        console.log('Senhas em atendimento/chamadas:', senhasAtendimentoOrdenadas);
        
        setSenhasAtivas(senhasAtivasOrdenadas);
        setSenhasEmAtendimento(senhasAtendimentoOrdenadas);
        setErro(null);
        
        // Calcular estatísticas
        const preferenciaisEmEspera = senhasAtivasOrdenadas.filter(senha => senha.preferencial).length;
        
        // Atualizar estatísticas
        setEstatisticas(prev => ({
          ...prev,
          preferenciaisEspera: preferenciaisEmEspera
        }));
      } else {
        setErro('Falha ao buscar senhas');
      }
    } catch (error) {
      setErro('Erro ao conectar ao servidor');
      console.error('Erro ao buscar senhas:', error);
    } finally {
      setCarregando(false);
    }
  }, []);
  
  // Função para buscar estatísticas
  const buscarEstatisticas = useCallback(async () => {
    try {
      // Buscar estatísticas da API
      const response = await api.get('/senhas/estatisticas');
      const data = await response.data;
      
      if (data.success) {
        setEstatisticas(prev => ({
          ...prev,
          tempoMedioEspera: data.data.tempoMedioEspera || '0 min',
          senhasChamadasHoje: data.data.senhasFinalizadas || 0
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  }, []);
  
  // Efeito para buscar senhas e estatísticas ao montar o componente
  useEffect(() => {
    buscarSenhas();
    buscarEstatisticas();
    
    // Configurar intervalo para atualização automática
    const intervaloSenhas = setInterval(buscarSenhas, 10000); // Atualizar a cada 10s
    const intervaloEstatisticas = setInterval(buscarEstatisticas, 30000); // Atualizar a cada 30s
    
    return () => {
      clearInterval(intervaloSenhas);
      clearInterval(intervaloEstatisticas);
    };
  }, [buscarSenhas, buscarEstatisticas]);
  
  // Efeito para configurar o socket
  useEffect(() => {
    // Conectar ao socket se ainda não estiver conectado
    socketService.connect();
    
    // Registrar ouvintes para atualizações
    const handleSenhaAtualizada = () => {
      buscarSenhas();
      buscarEstatisticas();
    };
    
    // ==========================================
    // HANDLERS DE TRANSFERÊNCIA DE SENHAS
    // ==========================================

    // Handler para quando uma senha é transferida para o usuário atual
    const handleSenhaTransferida = (data) => {
      console.log('🚨 CRÍTICO: Senha transferida recebida pelo WebSocket no Painel Recepção!', {
        data,
        userId: user?.id,
        timestamp: new Date().toISOString()
      });
      
      if (data.senha) {
        // CORREÇÃO: Remover notificação duplicada - já está sendo tratada no socketService
        // As notificações de transferência agora são centralizadas no socketService via notificationManager
        console.log('📤 Notificação de transferência já sendo tratada no socketService - PainelRecepcao');

        console.log('🔄 Atualizando dados após receber transferência no Painel Recepção...');
        
        // Atualizar listas de senhas
        buscarSenhas();
        buscarEstatisticas();
        
        console.log('✅ Dados atualizados após transferência no Painel Recepção!');
      } else {
        console.warn('⚠️ Evento senhaTransferida sem dados de senha:', data);
      }
    };

    // Handler para confirmação de transferência enviada
    const handleTransferenciaConfirmada = (data) => {
      console.log('✅ Transferência confirmada no Painel Recepção:', data);
      
      if (data.destinatario) {
        toast.success(
          `✅ Senha transferida para ${data.destinatario.nome} com sucesso`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );

        // Atualizar listas após transferência bem-sucedida
        buscarSenhas();
        buscarEstatisticas();
      }
    };

    // Handler para erro de transferência
    const handleErroTransferencia = (data) => {
      console.error('❌ Erro na transferência no Painel Recepção:', data);
      
      toast.error(
        `❌ Erro ao transferir senha: ${data.error}`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    };
    
    socketService.on('senhaAtualizada', handleSenhaAtualizada);
    socketService.on('senhaChamada', handleSenhaAtualizada);
    socketService.on('senhaEmAtendimento', handleSenhaAtualizada);
    socketService.on('senhaFinalizada', handleSenhaAtualizada);
    
    // Registrar listeners de transferência
    socketService.on('senhaTransferida', handleSenhaTransferida);
    socketService.on('transferenciaConfirmada', handleTransferenciaConfirmada);
    socketService.on('erroTransferencia', handleErroTransferencia);
    
    // ✅ CRÍTICO: Garantir que o usuário está conectado à sua room se já estiver conectado
    if (user?.id && socketService.isConnected()) {
      console.log(`👤 Painel Recepção: Socket já conectado, subscrevendo usuário ${user.id} à sua room: atendente:${user.id}`);
      socketService.subscribeToUser(user.id);
    }
    
    // Limpar ao desmontar
    return () => {
      socketService.off('senhaAtualizada', handleSenhaAtualizada);
      socketService.off('senhaChamada', handleSenhaAtualizada);
      socketService.off('senhaEmAtendimento', handleSenhaAtualizada);
      socketService.off('senhaFinalizada', handleSenhaAtualizada);
      socketService.off('senhaTransferida', handleSenhaTransferida);
      socketService.off('transferenciaConfirmada', handleTransferenciaConfirmada);
      socketService.off('erroTransferencia', handleErroTransferencia);
    };
  }, [buscarSenhas, buscarEstatisticas, user]);
  
  // Função para exclur uma senha
  const handleExcluirSenha = async (senhaId) => {
    // Verificar permissão antes de executar
    if (!podeExecutarAcaoAdministrativa()) {
      toast.error('Você não tem permissão para excluir senhas.');
      return;
    }

    try {
      console.log('Excluindo senha:', senhaId);
      
      // Usar o endpoint de cancelar senha
      const response = await api.post(`/senhas/${senhaId}/cancelar`);
      
      const data = await response.data;
      console.log('Resposta da exclusão:', data);
      
      if (data.success) {
        toast.success('Senha excluída com sucesso!');
        buscarSenhas();
      } else {
        toast.error(data.message || 'Erro ao excluir senha');
      }
    } catch (error) {
      toast.error('Erro ao conectar ao servidor');
      console.error('Erro ao excluir senha:', error);
    }
  };
  
  // Função para enviar senha para um atendente
  const handleEnviarParaAtendente = async (senhaId, atendente) => {
    // Verificar permissão antes de executar
    if (!podeExecutarAcaoAdministrativa()) {
      toast.error('Você não tem permissão para transferir senhas.');
      return;
    }

    // 🔍 DIAGNÓSTICO: Verificar estado do usuário
    console.group('🔄 PAINEL RECEPÇÃO - INÍCIO DA TRANSFERÊNCIA');
    console.log('📋 PAINEL RECEPÇÃO: senhaId recebido:', senhaId);
    console.log('👤 PAINEL RECEPÇÃO: atendente recebido:', atendente);
    console.log('🆔 PAINEL RECEPÇÃO: atendente.id:', atendente?.id);
    console.log('📛 PAINEL RECEPÇÃO: atendente.nome:', atendente?.nome);
    console.log('🏷️ PAINEL RECEPÇÃO: typeof atendente:', typeof atendente);
    const diagnostico = diagnosticarUsuario(user, 'PainelRecepcao.handleEnviarParaAtendente');
    console.log('📋 Parâmetros recebidos:', { senhaId, atendente });
    console.groupEnd();

    if (transferindo) {
      toast.warn('Transferência já em andamento. Aguarde...');
      return;
    }

    // ✅ VALIDAÇÃO CRÍTICA: Verificar se o usuário pode transferir
    if (!podeTransferir(user)) {
      console.error('❌ Painel Recepção: Usuário não pode realizar transferências');
      toast.error('Erro: Você não tem permissão para transferir senhas. Faça login novamente.');
      return;
    }

    // ✅ VALIDAÇÃO CRÍTICA: Verificar dados obrigatórios
    if (!senhaId) {
      console.error('❌ Painel Recepção: ID da senha não fornecido para transferência');
      toast.error('Erro: ID da senha não encontrado.');
      return;
    }

    // ✅ VALIDAÇÃO E NORMALIZAÇÃO DO ATENDENTE: Aceitar tanto objeto quanto ID
    let atendenteDestino;
    if (typeof atendente === 'number') {
      // Se recebeu apenas o ID, criar objeto básico
      atendenteDestino = {
        id: atendente,
        nome: `Atendente ${atendente}`,
        guiche: 1 // Valor padrão
      };
      console.log('🔄 Painel Recepção: Atendente normalizado de ID para objeto:', atendenteDestino);
    } else if (typeof atendente === 'object' && atendente !== null) {
      // Se recebeu um objeto, verificar se tem ID
      if (!atendente.id) {
        console.error('❌ Painel Recepção: Objeto atendente sem propriedade ID:', atendente);
        toast.error('Erro: Atendente de destino não tem ID válido.');
        return;
      }
      atendenteDestino = atendente;
    } else {
      console.error('❌ Painel Recepção: Atendente destino inválido (nem número nem objeto):', atendente);
      toast.error('Erro: Atendente de destino não identificado.');
      return;
    }

    console.log('✅ Painel Recepção: Atendente de destino validado:', atendenteDestino);

    setTransferindo(true);

    try {
      console.log('🔄 Painel Recepção: Iniciando transferência de senha:', { 
        senhaId, 
        atendenteOrigemId: user.id,
        atendenteDestinoId: atendenteDestino.id,
        guiche: atendenteDestino.guiche || 1
      });
      
      // Verificar se WebSocket está disponível
      if (socketService.isConnected()) {
        console.log('✅ WebSocket conectado, usando transferência em tempo real no Painel Recepção');
        
        try {
          // Usar o método de transferência com fallback automático
          const resultado = await socketService.transferirSenhaComFallback(
            senhaId,
            user.id, // ID do atendente que está transferindo
            atendenteDestino.id, // ID do atendente destino
            atendenteDestino.guiche || 1, // Guichê do atendente destino
            null // Motivo da transferência (opcional)
          );

          console.log('✅ Transferência bem-sucedida via WebSocket no Painel Recepção:', resultado);
          
          // Atualizar a lista de senhas
          buscarSenhas();
          buscarEstatisticas();

          // ✅ Notificação removida - será tratada pelo useAtendimentoHandlers
          console.log('✅ Transferência WebSocket realizada com sucesso no Painel Recepção');
          return;

        } catch (wsError) {
          console.warn('❌ Falha na transferência via WebSocket no Painel Recepção:', wsError.message);
          toast.warn('Falha na transferência em tempo real. Tentando método alternativo...');
        }
      }

      // Fallback HTTP se WebSocket falhar ou não estiver disponível
      console.log('🔄 Painel Recepção: Usando fallback HTTP para transferência');
      
      // Verificação adicional: buscar status atual do atendente antes de enviar
      const responseStatusAtendente = await api.get(`/senhas/atendente/${atendenteDestino.id}/em-atendimento`);
      
      if (responseStatusAtendente.data.success && responseStatusAtendente.data.data) {
        // Atendente tem senha em atendimento
        const senhaAtual = responseStatusAtendente.data.data;
        toast.error(`O atendente ${atendenteDestino.nome} já está ocupado atendendo a senha ${senhaAtual.numero}. Selecione outro atendente.`);
        return;
      }
      
      // Usar o endpoint HTTP de transferência
      const response = await api.post(`/senhas/${senhaId}/transferir`, { 
        senhaId,
        atendenteOrigemId: user.id,
        atendenteDestinoId: atendenteDestino.id,
        guiche: atendenteDestino.guiche || 1,
        motivo: null
      });
      
      const data = await response.data;
      console.log('✅ Resposta da transferência HTTP no Painel Recepção:', data);
      
      if (data && data.success) {
        // ✅ Notificação removida - será tratada pelo useAtendimentoHandlers
        console.log('✅ Transferência HTTP realizada com sucesso no Painel Recepção');
        
        // Atualizar a lista de senhas
        buscarSenhas();
        buscarEstatisticas();
      } else {
        // Melhor tratamento do erro baseado na resposta
        const errorMessage = data?.error || data?.message || 'Erro desconhecido ao transferir senha';
        console.error('❌ Erro detalhado na transferência:', data);
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error('❌ Erro na transferência no Painel Recepção:', error);
      
      // Se o erro for 404 ou similar, significa que o atendente não tem senha em atendimento (está disponível)
      if (error.response?.status === 404 || error.response?.data?.message?.includes('Nenhuma senha em atendimento')) {
        try {
          // Prosseguir com o envio da senha usando método legado
          const response = await api.post(`/senhas/${senhaId}/chamar`, { 
            atendenteId: atendenteDestino.id,
            guiche: atendenteDestino.guiche || 1,
            iniciarAtendimento: false
          });
          
          const data = await response.data;
          console.log('✅ Resposta do envio legado para atendente no Painel Recepção:', data);
          
          if (data.success) {
            toast.success(`Senha enviada para ${atendenteDestino.nome}! O atendente precisará iniciar o atendimento manualmente.`);
            buscarSenhas();
            buscarEstatisticas();
          } else {
            toast.error(data.message || 'Erro ao enviar senha para o atendente');
          }
        } catch (secondError) {
          toast.error('Erro ao conectar ao servidor. Verifique sua conexão.');
          console.error('Erro ao enviar senha para atendente (segunda tentativa):', secondError);
        }
      } else {
        // Mostrar mensagem de erro mais específica baseada no tipo de erro
        if (error.message && error.message.includes('obrigatório')) {
          toast.error(`Erro de validação: ${error.message}`);
        } else {
          toast.error(error.message || 'Erro ao transferir senha. Tente novamente.');
        }
        console.error('Erro na transferência:', error);
      }
    } finally {
      setTransferindo(false);
    }
  };
  
  // Função para resetar o painel de senhas
  const handleResetPainel = async () => {
    // Verificar permissão antes de executar
    if (!podeExecutarAcaoAdministrativa()) {
      toast.error('Você não tem permissão para resetar o painel de senhas.');
      setDialogResetOpen(false);
      return;
    }

    try {
      console.log('Resetando painel de senhas');
      
      // Usar o endpoint de arquivar que move as senhas para o histórico
      const response = await api.post('/senhas/arquivar');
      
      const data = await response.data;
      console.log('Resposta do reset do painel:', data);
      
      if (data.success) {
        toast.success(`Painel de senhas resetado com sucesso! ${data.arquivadas || 0} senhas movidas para o histórico.`);
        buscarSenhas();
        buscarEstatisticas();
      } else {
        toast.error(data.message || 'Erro ao resetar painel de senhas');
      }
    } catch (error) {
      toast.error('Erro ao conectar ao servidor');
      console.error('Erro ao resetar painel de senhas:', error);
    } finally {
      setDialogResetOpen(false);
    }
  };
  
  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar sidebarOpen={sidebarOpen} customTheme={customTheme} />
        
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Header 
            title="Painel de Recepção" 
            sidebarOpen={sidebarOpen}
            handleSidebarToggle={toggleSidebar}
            customTheme={customTheme}
          />
          
          <Container maxWidth="xxl" sx={{ mt: 10, overflow: 'hidden' }}>
            {/* Botão de Reset - apenas para usuários não atendentes */}
            {podeExecutarAcaoAdministrativa() && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setDialogResetOpen(true)}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                >
                  Resetar Painel de Senhas
                </Button>
              </Box>
            )}
            
            {/* Cards de estatísticas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard
                  title="Tempo Médio de Espera"
                  value={estatisticas.tempoMedioEspera}
                  icon={<AccessTimeIcon />}
                  color="#4CAF50" // Verde
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard
                  title="Senhas Chamadas Hoje"
                  value={estatisticas.senhasChamadasHoje}
                  icon={<CheckCircleIcon />}
                  color="#2196F3" // Azul
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard
                  title="Preferenciais em Espera"
                  value={estatisticas.preferenciaisEspera}
                  icon={<AccessibilityIcon />}
                  color="#F44336" // Vermelho
                />
              </Grid>
            </Grid>
            
            {/* Seções de Senhas Ativas e Em Atendimento */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={7} sx={{ height: '75vh' }}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  boxShadow: theme => theme.shadows[3],
                  height: '90%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 2 }}>
                    Senhas Aguardando Chamada
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {carregando && senhasAtivas.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <SenhasAtivasList 
                      senhas={senhasAtivas} 
                      onExcluir={podeExecutarAcaoAdministrativa() ? handleExcluirSenha : null}
                      onEnviarParaAtendente={podeExecutarAcaoAdministrativa() ? handleEnviarParaAtendente : null}
                    />
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={5}  sx={{ height: '75vh' }}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  boxShadow: theme => theme.shadows[3],
                  height: '90%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: theme => alpha(theme.palette.primary.main, 0.02),
                  overflow: 'hidden'
                }}>
                  <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 2, flexShrink: 0 }}>
                    Senhas Chamadas e em Atendimento
                  </Typography>
                  <Divider sx={{ mb: 2, flexShrink: 0 }} />
                  
                  {carregando && senhasEmAtendimento.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      flexGrow: 1
                    }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden'
                    }}>
                      <SenhasAtendimentoList senhas={senhasEmAtendimento} />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      
      {/* Diálogo de confirmação para reset do painel */}
      <Dialog
        open={dialogResetOpen}
        onClose={() => setDialogResetOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar Reset do Painel de Senhas
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta ação irá mover todas as senhas atuais para o histórico e limpar o painel de senhas ativas.
            <br />
            <br />
            <strong>Esta operação não pode ser desfeita.</strong> Tem certeza que deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogResetOpen(false)} 
            color="primary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleResetPainel} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Confirmar Reset
          </Button>
        </DialogActions>
      </Dialog>
      
      <ToastContainer position="bottom-right" />
    </ThemeProvider>
  );
};

export default PainelRecepcao; 